import { motion } from "framer-motion";
import { ShieldAlert, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { Logo } from "@/components/Logo";

const sectionsIt = [
  {
    title: "1. Il nostro impegno",
    body: [
      "FuffyFam si impegna a garantire un ambiente sicuro per tutti i suoi utenti. Non tolleriamo in alcun modo contenuti inappropriati che coinvolgono minori, inclusi — a titolo non esaustivo — abusi, sfruttamento o adescamento di minori (CSAE), e qualsiasi contenuto che sessualizzi o metta in pericolo bambini e ragazzi.",
      "Qualsiasi contenuto o comportamento di questo tipo comporta la rimozione immediata e la sospensione permanente dell'account responsabile.",
    ],
  },
  {
    title: "2. Requisito di età",
    body: [
      "FuffyFam è un servizio riservato a utenti di almeno 16 anni. Non raccogliamo consapevolmente dati di minori e, se veniamo a conoscenza di un account registrato da un minore, lo eliminiamo immediatamente.",
    ],
  },
  {
    title: "3. Sistema di segnalazione",
    body: [
      "Nell'app è disponibile un sistema di segnalazione accessibile da ogni profilo, contenuto e conversazione. Ogni segnalazione che coinvolge la sicurezza dei minori viene trattata con la massima priorità dal nostro team di moderazione.",
      "Puoi segnalarci contenuti o comportamenti sospetti anche via email, in qualsiasi momento.",
    ],
  },
  {
    title: "4. Collaborazione con le autorità",
    body: [
      "In caso di segnalazioni che riguardano abusi o sfruttamento di minori, collaboriamo pienamente con le autorità competenti, incluse le forze dell'ordine e gli organismi nazionali e internazionali preposti alla protezione dell'infanzia, come previsto dalla legge.",
      "Conserviamo e mettiamo a disposizione delle autorità le informazioni necessarie alle indagini, nel rispetto della normativa applicabile.",
    ],
  },
  {
    title: "5. Contatti",
    body: [
      "Per segnalazioni relative alla sicurezza dei minori scrivici a supporto.fuffyfam@gmail.com. Prendiamo in carico ogni segnalazione con priorità e riservatezza.",
    ],
  },
];

const sectionsEn = [
  {
    title: "1. Our commitment",
    body: [
      "FuffyFam is committed to providing a safe environment for all its users. We do not tolerate, in any form, inappropriate content involving minors, including — without limitation — child sexual abuse, exploitation or grooming (CSAE), and any content that sexualizes or endangers children.",
      "Any such content or behaviour results in immediate removal and permanent suspension of the responsible account.",
    ],
  },
  {
    title: "2. Age requirement",
    body: [
      "FuffyFam is intended for users aged 16 and over. We do not knowingly collect data from minors and, if we become aware of an account registered by a minor, we delete it immediately.",
    ],
  },
  {
    title: "3. Reporting system",
    body: [
      "The app includes a reporting system accessible from every profile, piece of content and conversation. Every report involving child safety is handled with the highest priority by our moderation team.",
      "You can also report suspicious content or behaviour to us by email at any time.",
    ],
  },
  {
    title: "4. Cooperation with authorities",
    body: [
      "When reports concern child abuse or exploitation, we fully cooperate with the competent authorities, including law enforcement and national and international child protection organisations, as required by law.",
      "We preserve and make available to the authorities the information needed for investigations, in compliance with applicable regulations.",
    ],
  },
  {
    title: "5. Contact",
    body: [
      "For reports related to child safety, write to us at supporto.fuffyfam@gmail.com. We handle every report with priority and confidentiality.",
    ],
  },
];

const Section = ({ s, i, lang }) => (
  <motion.section
    initial={{ opacity: 0, y: 24 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.7, delay: 0.05 * (i % 3), ease: [0.16, 1, 0.3, 1] }}
    data-testid={`child-safety-section-${lang}-${i + 1}`}
  >
    <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tight">{s.title}</h2>
    {s.body.map((p, j) => (
      <p key={j} className="mt-4 text-base leading-relaxed text-[#5C5C5C]">
        {p}
      </p>
    ))}
  </motion.section>
);

export default function ChildSafety() {
  return (
    <div className="min-h-screen bg-[#F9F8F6] text-[#1A1A1A]" data-testid="child-safety-page">
      <nav className="flex items-center justify-between px-6 sm:px-10 lg:px-16 py-6 border-b border-[#1A1A1A]/[0.06]">
        <Logo testId="child-safety-logo" />
        <Link
          to="/"
          data-testid="child-safety-back-link"
          className="flex items-center gap-2 text-sm font-semibold text-[#5C5C5C] hover:text-[#FF6B35] transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Torna alla home
        </Link>
      </nav>

      <main className="px-6 sm:px-10 lg:px-16 py-16 lg:py-24 max-w-3xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#FF6B35] mb-6">
            <ShieldAlert className="w-4 h-4" />
            Sicurezza dei minori · Child Safety
          </p>
          <h1
            data-testid="child-safety-title"
            className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
          >
            Politiche sulla sicurezza dei minori
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
            La protezione dei minori è una priorità assoluta per FuffyFam. Questa pagina descrive
            le nostre politiche in italiano e in inglese. Ultimo aggiornamento: settembre 2026.
          </p>
        </motion.div>

        <div className="mt-14 space-y-12">
          {sectionsIt.map((s, i) => (
            <Section key={s.title} s={s} i={i} lang="it" />
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 pt-14 border-t border-[#1A1A1A]/[0.08]"
        >
          <h2
            data-testid="child-safety-title-en"
            className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight"
          >
            Child Safety Policy
          </h2>
          <p className="mt-4 text-base text-[#5C5C5C] leading-relaxed">
            The English version of our child safety policies. Last updated: September 2026.
          </p>
          <div className="mt-10 space-y-12">
            {sectionsEn.map((s, i) => (
              <Section key={s.title} s={s} i={i} lang="en" />
            ))}
          </div>
        </motion.div>
      </main>

      <footer className="border-t border-[#1A1A1A]/[0.08] px-6 sm:px-10 lg:px-16 py-10 flex flex-col sm:flex-row items-center justify-between gap-4 max-w-3xl mx-auto">
        <p className="text-sm text-[#5C5C5C]">© 2026 FuffyFam · fuffyfam.com</p>
        <a
          href="mailto:supporto.fuffyfam@gmail.com"
          data-testid="child-safety-contact-link"
          className="text-sm font-semibold text-[#FF6B35] hover:text-[#E55A2B] transition-colors duration-300"
        >
          supporto.fuffyfam@gmail.com
        </a>
      </footer>
    </div>
  );
}
